import { reactive } from "vue";
import { Emitter } from "Util/emitter";

export function eventIsInsideRect(rect, dragEvent) {
    const x = dragEvent.clientX;
    const y = dragEvent.clientY;
    const xIn = x >= rect.left && x <= rect.left + rect.width;
    const yIn = y >= rect.top && y <= rect.top + rect.height;
    return xIn && yIn;
}
export function createOrderableHub() {
    const emitter = new Emitter();
    return reactive({
        dragItemKey: null,
        dragEvent: null,
        dragRect: null,
        dragOrderable: null,
        dropOrderable: null,
        on: (...args) => emitter.on(...args),
        off: (...args) => emitter.off(...args),
        emit: (...args) => emitter.emit(...args)
    });
}

// orderable hub publish mixins
export const HUB_PUBLISH_MIXIN = {
    methods: {
        orderableHubDragMove(dragEvent) {
            if (this.orderableHub) {
                this.orderableHub.dropOrderable = null;
                this.orderableHub.dragOrderable = this;
                this.orderableHub.dragEvent = dragEvent;
                this.orderableHub.dragRect = this.draggingStartRect;
                this.orderableHub.dragItemKey = this.dragItemKey;

                this.orderableHub.emit("orderableHub_dragMove");
            }
        },
        orderableHubDrop() {
            if (this.orderableHub) {
                this.orderableHub.emit("orderableHub_drop", {
                    orderable: this,
                    itemKey: this.dragItemKey
                });
                this.orderableHub.dragItemKey = null;
                this.orderableHub.dragEvent = null;
                this.orderableHub.dragRect = null;
                this.orderableHub.dragOrderable = null;
                this.orderableHub.dropOrderable = null;
            }
        },
        orderableHubDropMultiple(itemKeys) {
            if (this.orderableHub) {
                this.orderableHub.emit("orderableHub_drop_multiple", {
                    orderable: this,
                    itemKeys: itemKeys
                });
                this.orderableHub.dragItemKey = null;
                this.orderableHub.dragEvent = null;
                this.orderableHub.dragRect = null;
                this.orderableHub.dragOrderable = null;
                this.orderableHub.dropOrderable = null;
            }
        }
    }
};

// orderable hub subscription mixins
export const HUB_SUBSCRIBE_MIXIN = {
    created() {
        if (this.orderableHub) {
            this.orderableHub.on("orderableHub_dragMove", this.onOrderableHubDragMove);
            this.orderableHub.on("orderableHub_drop", this.onOrderableHubDrop);
            this.orderableHub.on("orderableHub_drop_multiple", this.onOrderableHubDropMultiple);
        }
    },
    beforeUnmounted() {
        if (this.orderableHub) {
            this.orderableHub.off("orderableHub_dragMove", this.onOrderableHubDragMove);
            this.orderableHub.off("orderableHub_drop", this.onOrderableHubDrop);
            this.orderableHub.off("orderableHub_drop_multiple", this.onOrderableHubDropMultiple);
        }
    },
    computed: {
        externalItemAcceptable() {
            if (!this.orderableHub || !this.orderableHub.dragEvent) {
                return false;
            }
            if (this.orderableHub.dragOrderable === this) {
                return false;
            }
            if (typeof this.externalItemValidator === "function") {
                if (!this.externalItemValidator(this.orderableHub.dragItemKey)) {
                    return false;
                }
            }

            const hasKey = this.internalItemKeys.includes(this.orderableHub.dragItemKey);
            return !hasKey;
        },
        acceptExternalItem() {
            if (!this.externalItemAcceptable) {
                return false;
            }
            if (this.$el) {
                const rect = this.$el.getBoundingClientRect();
                const inside = eventIsInsideRect(rect, this.orderableHub.dragEvent);
                return inside;
            }
            return false;
        }
    },
    methods: {
        onOrderableHubDragMove() {
            if (this.acceptExternalItem) {
                this.orderableHub.dropOrderable = this;
                //add placeholder if necessary
                if (this.placeholderIndex < 0) {
                    this.internalItemKeys.splice(0, 0, this.PLACEHOLDER_KEY);
                    this.draggingStartRect = this.orderableHub.dragRect;
                }
                this.doDragMove(this.orderableHub.dragItemKey, this.orderableHub.dragEvent);
            } else if (this.orderableHub.dragOrderable !== this) {
                //remove placeholder if present
                if (this.placeholderIndex >= 0) {
                    this.internalItemKeys.splice(this.placeholderIndex, 1);
                    this.draggingStartRect = null;
                }
            }
        },

        onOrderableHubDrop({ orderable, itemKey }) {
            if (orderable === this) {
                if (this.itemAcceptedExternally) {
                    this.del(itemKey);
                }
            } else {
                if (this.acceptExternalItem) {
                    this.internalItemKeys.splice(this.placeholderIndex, 1, itemKey);
                    this.$emit("update:modelValue", this.internalItemKeys);
                    this.draggingStartRect = null;
                }
            }
        },
        onOrderableHubDropMultiple({ orderable, itemKeys }) {
            if (orderable === this) {
                if (this.itemAcceptedExternally) {
                    for (let i = 0; i < itemKeys.length; i++) {
                        this.del(itemKeys[i]);
                    }
                }
            } else {
                if (this.acceptExternalItem) {
                    const index = this.placeholderIndex;
                    this.internalItemKeys.splice(index, 1, itemKeys[0]);
                    for (let i = 1; i < itemKeys.length; i++) {
                        this.internalItemKeys.splice(index + i, 0, itemKeys[i]);
                    }
                    this.$emit("update:modelValue", this.internalItemKeys);
                    this.draggingStartRect = null;
                }
            }
        }
    }
};

export const HUB_ACCEPT_EXTERNAL_MIXIN = {
    computed: {
        itemAcceptedExternally() {
            if (!this.orderableHub) {
                return false;
            }
            if (this.orderableHub.dropOrderable == null || this.orderableHub.dropOrderable === this) {
                return false;
            }
            return this.dragItemKey !== null;
        }
    }
};

export const ORDERABLE_HUB_MIXIN = {
    created: HUB_SUBSCRIBE_MIXIN.created,
    beforeUnmounted: HUB_SUBSCRIBE_MIXIN.beforeUnmounted,
    computed: { ...HUB_SUBSCRIBE_MIXIN.computed, ...HUB_ACCEPT_EXTERNAL_MIXIN.computed },
    methods: { ...HUB_SUBSCRIBE_MIXIN.methods, ...HUB_PUBLISH_MIXIN.methods }
};
